/**
 * Sprint 3.8.1 — Hook React para conectar no WS do Dashboard.
 *
 * Auto-reconect com backoff exponencial: 1s → 2s → 4s → 8s → 16s → 30s (cap).
 *
 * Uso:
 *
 *   const { status, lastEvent } = useDashboardWs((event) => {
 *     // event = { type: 'heartbeat'|'ota'|'rollout'|'maintenance'|'ping', ts, payload }
 *     if (event.type === 'heartbeat') { ... }
 *     if (event.type === 'ota') { ... }
 *     ...
 *   });
 *
 * status: 'connecting' | 'open' | 'closed' | 'error'
 * lastEvent: último evento recebido (útil para debug)
 *
 * Quando o usuário não tem ADMIN_TOKEN configurado, o WS nem tenta
 * conectar (URL sem token) — fallback de polling no caller é
 * responsável por manter o dashboard funcionando.
 */
import { useEffect, useRef, useState } from "react";

import { getDashboardWsUrl } from "../lib/api.js";

const MAX_BACKOFF_MS = 30_000;
const INITIAL_BACKOFF_MS = 1_000;

export function useDashboardWs(onEvent) {
  const [status, setStatus] = useState("connecting"); // connecting|open|closed|error
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const backoffRef = useRef(INITIAL_BACKOFF_MS);
  const onEventRef = useRef(onEvent);
  const mountedRef = useRef(true);

  // Mantém callback atualizado sem reconectar
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    mountedRef.current = true;

    function connect() {
      if (!mountedRef.current) return;

      let url;
      try {
        url = getDashboardWsUrl();
      } catch (e) {
        setStatus("error");
        return;
      }

      // Se a URL termina com token=&  (token vazio), não tenta
      if (/token=$/.test(url) || /token=&/.test(url) || url.includes("?token=&") || url.endsWith("token=")) {
        setStatus("error");
        return;
      }

      setStatus("connecting");
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        setStatus("error");
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setStatus("open");
        backoffRef.current = INITIAL_BACKOFF_MS; // reset backoff em conexão OK
      };

      ws.onmessage = (msgEvent) => {
        if (!mountedRef.current) return;
        try {
          const parsed = JSON.parse(msgEvent.data);
          // Ignora pings do server (mantém só para keepalive)
          if (parsed.type === "ping") return;
          setLastEvent(parsed);
          if (typeof onEventRef.current === "function") {
            onEventRef.current(parsed);
          }
        } catch (e) {
          // Mensagem mal-formada — ignora
        }
      };

      ws.onerror = () => {
        if (!mountedRef.current) return;
        setStatus("error");
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setStatus("closed");
        scheduleReconnect();
      };
    }

    function scheduleReconnect() {
      if (!mountedRef.current) return;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      const delay = backoffRef.current;
      reconnectTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        // Dobra o backoff (cap em 30s)
        backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
        connect();
      }, delay);
    }

    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const ws = wsRef.current;
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        try {
          ws.close();
        } catch (e) {
          // ignore
        }
      }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // conecta uma vez no mount

  return { status, lastEvent };
}
